import React, { useState, useEffect } from 'react';
import { useUserProfile } from '../../hooks/useUserProfile';
import enhancedApiClient from '../../api/enhancedApiClient';

const VendorSettings = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    details: '',
    dueOffsetDays: 30,
    backgroundImage: null
  });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile } = useUserProfile(currentUser?.username);
  const vendorId = userProfile?.currentVendorId;

  useEffect(() => {
    if (vendorId && activeSection === 'checklist') {
      fetchTemplates();
    }
  }, [vendorId, activeSection]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/templates`);
      const data = response.ok ? await response.json() : [];
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      let backgroundImageUrl = null;
      
      // Upload background image if provided
      if (templateForm.backgroundImage && typeof templateForm.backgroundImage !== 'string') {
        const uploadResult = await enhancedApiClient.uploadFile(templateForm.backgroundImage);
        backgroundImageUrl = uploadResult.fileUrl;
      } else if (typeof templateForm.backgroundImage === 'string') {
        backgroundImageUrl = templateForm.backgroundImage;
      }
      
      const templateData = {
        name: templateForm.name,
        details: templateForm.details,
        dueOffsetDays: templateForm.dueOffsetDays,
        backgroundImageUrl: backgroundImageUrl
      };
      
      if (editingTemplate) {
        await enhancedApiClient.updateTaskTemplate(vendorId, editingTemplate.id, templateData);
      } else {
        await enhancedApiClient.createTaskTemplate({
          vendorId,
          ...templateData,
          createdAt: new Date().toISOString()
        });
      }
      fetchTemplates();
      setShowAddTemplate(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', details: '', dueOffsetDays: 30, backgroundImage: null });
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await enhancedApiClient.deleteTaskTemplate(vendorId, templateId);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const iconOptions = ['📋', '📝', '✅', '📅', '💰', '🎵', '📸', '🍰', '💐', '🎉', '📄', '✉️', '🎨', '🏠', '🚗'];
  const colorOptions = [
    { name: 'Mint', value: '#E8F4E8' },
    { name: 'Blue', value: '#E0F2FE' },
    { name: 'Purple', value: '#F3E8FF' },
    { name: 'Pink', value: '#FCE7F3' },
    { name: 'Yellow', value: '#FEF3C7' },
    { name: 'Orange', value: '#FFEDD5' },
    { name: 'Gray', value: '#F3F4F6' }
  ];

  const settingsItems = [
    { id: 'venues', title: 'Venues', description: 'Orakei Bay', icon: 'building' },
    { id: 'team', title: 'Team & Invitations', description: 'Invite users to your account', icon: 'users' },
    { id: 'checklist', title: 'Default Checklist', description: 'Task templates for new events', icon: 'clipboard' },
    { id: 'payments', title: 'Payments', description: 'Tax, Bank Account, Currency', icon: 'dollar-sign' },
    { id: 'account', title: 'Account', description: 'Email, Password', icon: 'user' },
    { id: 'logout', title: 'Logout', description: '', icon: 'log-out' }
  ];

  if (activeSection === 'team') {
    return (
      <div>
        <div className="page-header" style={{
          backgroundColor: '#E8F4E8',
          padding: '48px 24px 80px 24px',
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          <button
            onClick={() => setActiveSection(null)}
            style={{
              position: 'absolute',
              left: '24px',
              top: '24px',
              background: 'none',
              border: 'none',
              color: '#6B9080',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back
          </button>
          <h1 className="page-title" style={{ 
            fontSize: '56px', 
            fontWeight: '400', 
            color: '#1F2937',
            margin: 0,
            fontFamily: 'Georgia, serif',
            letterSpacing: '-0.02em'
          }}>
            Team & Invitations
          </h1>
        </div>

        <div className="container" style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '16px' }}>Invite a User</h3>
            <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '20px' }}>
              Send an invitation to join your venue/vendor account.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Email Address *</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white'
                }}
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {inviteSuccess && (
              <div style={{ padding: '12px', backgroundColor: '#E8F4E8', borderRadius: '8px', color: '#065F46', fontSize: '14px', marginBottom: '16px' }}>
                {inviteSuccess}
              </div>
            )}

            <button
              disabled={inviteLoading || !inviteEmail}
              onClick={async () => {
                setInviteLoading(true);
                setInviteSuccess('');
                try {
                  const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/invite`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: inviteEmail, role: inviteRole })
                  });
                  if (response.ok) {
                    setInviteSuccess(`Invitation sent to ${inviteEmail}!`);
                    setInviteEmail('');
                  } else {
                    alert('Failed to send invitation. Please try again.');
                  }
                } catch (error) {
                  console.error('Error sending invite:', error);
                  alert('Failed to send invitation.');
                } finally {
                  setInviteLoading(false);
                }
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: (!inviteEmail || inviteLoading) ? '#9CA3AF' : '#6B9080',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (!inviteEmail || inviteLoading) ? 'not-allowed' : 'pointer'
              }}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (activeSection === 'checklist') {
    return (
      <div>
        <div className="page-header" style={{
          backgroundColor: '#E8F4E8',
          padding: '48px 24px 80px 24px',
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          <button
            onClick={() => setActiveSection(null)}
            style={{
              position: 'absolute',
              left: '24px',
              top: '24px',
              background: 'none',
              border: 'none',
              color: '#6B9080',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back
          </button>
          <h1 className="page-title" style={{ 
            fontSize: '56px', 
            fontWeight: '400', 
            color: '#1F2937',
            margin: 0,
            fontFamily: 'Georgia, serif',
            letterSpacing: '-0.02em'
          }}>
            Default Checklist Templates
          </h1>
        </div>

        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ color: '#6B7280', fontSize: '14px' }}>
              These tasks will be automatically added to new events
            </p>
            <button
              onClick={() => {
                setShowAddTemplate(true);
                setEditingTemplate(null);
                setTemplateForm({ name: '', details: '', dueOffsetDays: 30, backgroundImage: null });
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6B9080',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              + Add Template
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {templates.map(template => (
              <div key={template.id} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '60px',
                  height: '40px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {template.backgroundImageUrl ? (
                    <img src={template.backgroundImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '16px', color: '#1F2937' }}>
                    {template.name}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '13px', marginTop: '2px' }}>
                    Due {template.dueOffsetDays} days before event
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditingTemplate(template);
                    setTemplateForm(template);
                    setShowAddTemplate(true);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#6B9080',
                    border: '1px solid #6B9080',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#DC2626',
                    border: '1px solid #DC2626',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>

        {showAddTemplate && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '500px',
              margin: '20px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
                {editingTemplate ? 'Edit Template' : 'Add Template'}
              </h2>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Task Name</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Details</label>
                <textarea
                  value={templateForm.details}
                  onChange={(e) => setTemplateForm({...templateForm, details: e.target.value})}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Due (days before event)</label>
                <input
                  type="number"
                  value={templateForm.dueOffsetDays}
                  onChange={(e) => setTemplateForm({...templateForm, dueOffsetDays: parseInt(e.target.value)})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Background Image <span style={{ color: '#9CA3AF' }}>(Optional)</span></label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && file.size > 5 * 1024 * 1024) {
                        alert('Image size must be less than 5MB');
                        e.target.value = '';
                        return;
                      }
                      setTemplateForm({...templateForm, backgroundImage: file});
                    }}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                    id="template-bg-upload"
                  />
                  <label
                    htmlFor="template-bg-upload"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #6B9080',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#6B9080',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {templateForm.backgroundImage ? 
                      (typeof templateForm.backgroundImage === 'string' ? 'Change Image' : templateForm.backgroundImage.name) 
                      : 'Choose Image'}
                  </label>
                </div>
                {templateForm.backgroundImage && (
                  <div style={{ marginTop: '12px', position: 'relative' }}>
                    <img 
                      src={typeof templateForm.backgroundImage === 'string' ? templateForm.backgroundImage : URL.createObjectURL(templateForm.backgroundImage)} 
                      alt="Preview" 
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setTemplateForm({...templateForm, backgroundImage: null})}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
                  Max 5MB. This image will appear on the task card.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowAddTemplate(false);
                    setEditingTemplate(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6B9080',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '48px 24px 80px 24px',
        textAlign: 'center',
        marginBottom: '60px'
      }}>
        <h1 className="page-title" style={{ 
          fontSize: '56px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          Settings
        </h1>
      </div>

      <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {settingsItems.map(item => (
            <div 
              key={item.id} 
              onClick={() => { if (item.id === 'checklist') setActiveSection('checklist'); if (item.id === 'team') setActiveSection('team'); }}
              style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              cursor: 'pointer'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#E8F4E8',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {item.icon === 'building' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <path d="M3 21h18"/>
                    <path d="M5 21V7l8-4v18"/>
                    <path d="M19 21V9.5l-6-2.5"/>
                  </svg>
                )}
                {item.icon === 'users' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                )}
                {item.icon === 'clipboard' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  </svg>
                )}
                {item.icon === 'dollar-sign' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                )}
                {item.icon === 'user' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
                {item.icon === 'log-out' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16,17 21,12 16,7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1F2937', marginBottom: '4px' }}>
                  {item.title}
                </div>
                {item.description && (
                  <div style={{ color: '#6B7280', fontSize: '14px' }}>
                    {item.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VendorSettings;